import {useAppDataStore} from "@/mobx/appDataStore.jsx";
import {DataTable} from "components/dataTable/index.jsx";
import {useEffect, useState, useMemo, useCallback} from "react";
import {observer} from "mobx-react";
import moment from "moment";
import formatters from "helpers/formatters.jsx";

export default observer(function AdminSales(props) {
  const appDataStore = useAppDataStore();
  const [refreshKey, setRefreshKey] = useState(0);

  const userScopes = appDataStore.user?.scopes || [];
  const hasMasterScope = Array.isArray(userScopes) && userScopes.includes('master');

  const StatusBadge = ({status}) => {
    const statusColors = {
      SUCCESS: "bg-green-100 text-green-800",
      ERROR: "bg-red-100 text-red-800",
      PENDING: "bg-yellow-100 text-yellow-800"
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[status] || "bg-gray-100 text-gray-800"}`}>
        {status}
      </span>
    );
  };

  const columns = useMemo(() => ([
    {
      accessorKey: 'id',
      header: 'ID',
      cell: ({cell}) => {
        return cell.getValue();
      }
    },
    {
      accessorKey: 'product',
      header: 'Producto',
      opFilter: 'eq',
    },
    {
      accessorKey: 'email',
      header: 'Email',
    },
    {
      accessorKey: 'customerName',
      header: 'Cliente',
    },
    {
      accessorKey: 'reference',
      header: 'Referencia',
    },
    {
      accessorKey: 'amount',
      header: 'Monto',
      cell: ({cell}) => formatters.cash(cell.getValue()),
      exportFormat: (row) => row.amount
    },
    {
      accessorKey: 'result',
      header: 'Estado',
      opFilter: 'eq',
      cell: ({cell}) => <StatusBadge status={cell.getValue()}/>
    },
    {
      accessorKey: 'transactionId',
      header: 'Transaction ID',
    },
    {
      accessorKey: 'novelty',
      header: 'Novedad',
      cell: ({cell}) => cell.getValue() ? 'SI' : '',
      exportFormat: (row) => row.novelty ? 'Sí' : 'No'
    },
    {
      accessorKey: 'createdAt',
      header: 'Fecha',
      cell: ({cell}) => moment(cell.getValue()).format('YYYY-MM-DD HH:mm:ss'),
      exportFormat: (row) => moment(row.createdAt).format('YYYY-MM-DD HH:mm:ss')
    }
  ]), []);

  const dataFetch = useCallback(async (
    records,
    currentPage,
    order,
    search,
    filter
  ) => {
    try {
      const requestData = {
        records,
        currentPage,
        order,
        search,
        filter,
        attributes: ['id', 'userId', 'amount', 'balanceBefore', 'balanceAfter', 'result', 'error', 'transactionId', 'reference', 'phoneNumber', 'email', 'customerName', 'novelty', 'product', 'createdAt'],
        searchAttributes: ['phoneNumber', 'email', 'customerName', 'reference', 'transactionId']
      };

      const response = await appDataStore.post("/sales/list-all", requestData);

      return {
        data: response.data || [],
        totalRows: response.total || 0,
        totalPages: response.totalPages || 1
      };
    } catch (err) {
      console.error("Error al cargar ventas:", err);
      return {
        data: [],
        totalRows: 0,
        totalPages: 1
      };
    }
  }, [appDataStore]);

  if (!hasMasterScope) {
    return (
      <>
        <div className="p-2 max-w-7xl mx-auto text-black">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">Historial de Ventas</h2>
          <div className="bg-red-50 border border-red-300 rounded-lg p-6 max-w-2xl">
            <div className="flex items-start gap-3">
              <span className="text-2xl">🔒</span>
              <div>
                <h3 className="text-lg font-bold text-red-800 mb-2">Acceso Denegado</h3>
                <p className="text-red-700">
                  No tienes permisos para acceder al historial completo de ventas. Se requiere el
                  scope <strong>Master</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="p-2 mx-auto text-black">
        <DataTable
          key={refreshKey}
          columns={columns}
          dataFetch={dataFetch}
          enablePagination
          enableSearch
          enableColumnFilter
          enableExport
          exportFileName="historial-ventas"
        />
      </div>
    </>
  );
});